import { useState } from 'react';
import {
   Box,
   Button,
   FormControl,
   FormHelperText,
   Grid,
   MenuItem,
   Select,
   TextField,
   Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../Components/ConfirmDialog';
import { useUpdateProviderContactMutation } from '../../Redux/Service/ProviderApi';
import { handleApiError } from '../../utils/apiErrorHandler';

const errorTextSx = { color: '#d32f2f', margin: '3px 14px' };

/**
 * Service Provider block shared by the quotation Add and Edit forms.
 * - Email / phone appear only after a provider is selected.
 * - "Change" appears when email/phone differ from the saved provider and,
 *   after confirmation, saves them to the provider itself. (Submitting the
 *   quotation also saves them, so Change is just an explicit shortcut.)
 */
function ServiceProviderSection({ serviceProviders, onProviderSaved }) {
   const { values, setFieldValue, setFieldTouched, touched, errors } = useFormikContext();
   const [updateContact, { isLoading }] = useUpdateProviderContactMutation();
   const [confirmOpen, setConfirmOpen] = useState(false);

   const selectedId = values.serviceProviderId === '' ? null : Number(values.serviceProviderId);
   const saved = serviceProviders.find((p) => p.id === selectedId);

   const email = (values.serviceProviderEmail || '').trim();
   const phone = (values.serviceProviderPhone || '').trim();
   const changed = !!saved && (email !== (saved.email || '') || phone !== (saved.phoneNo || ''));
   const invalid = Boolean(errors.serviceProviderEmail || errors.serviceProviderPhone);

   const handleSelect = (event) => {
      const id = event.target.value === '' ? '' : Number(event.target.value);
      const sp = serviceProviders.find((s) => s.id === id);
      setFieldValue('serviceProviderId', id);
      setFieldValue('serviceProviderName', sp?.name || '');
      setFieldValue('serviceProviderEmail', sp?.email || '');
      setFieldValue('serviceProviderPhone', sp?.phoneNo || '');
      setFieldTouched('serviceProviderEmail', false);
      setFieldTouched('serviceProviderPhone', false);
   };

   const handleChange = async () => {
      try {
         const res = await updateContact({ id: saved.id, email, phoneNo: phone }).unwrap();
         onProviderSaved?.(res.response);
         setFieldValue('serviceProviderEmail', email);
         setFieldValue('serviceProviderPhone', phone);
         toast.success(res.message || 'Service Provider updated');
         setConfirmOpen(false);
      } catch (error) {
         handleApiError(error);
      }
   };

   const fieldError = (name) => touched[name] && errors[name];

   return (
      <Box className="commonBox">
         <Typography variant="h6" gutterBottom>
            Service Provider*
         </Typography>
         <Grid container spacing={2} alignItems="flex-start">
            <Grid item md={4} xs={12}>
               <label htmlFor="serviceProviderId">Service Provider*</label>
               <FormControl fullWidth error={Boolean(fieldError('serviceProviderId'))}>
                  <Select
                     id="serviceProviderId"
                     name="serviceProviderId"
                     displayEmpty
                     value={values.serviceProviderId ?? ''}
                     onChange={handleSelect}
                     onBlur={() => setFieldTouched('serviceProviderId', true)}
                  >
                     <MenuItem value="">Select</MenuItem>
                     {serviceProviders.map((sp) => (
                        <MenuItem key={sp.id} value={sp.id}>
                           {sp.name}
                        </MenuItem>
                     ))}
                  </Select>
                  {fieldError('serviceProviderId') && (
                     <FormHelperText sx={errorTextSx}>{errors.serviceProviderId}</FormHelperText>
                  )}
               </FormControl>
            </Grid>

            {saved && (
               <>
                  <Grid item md={4} xs={12}>
                     <label htmlFor="serviceProviderEmail">Email Address*</label>
                     <TextField
                        fullWidth
                        id="serviceProviderEmail"
                        name="serviceProviderEmail"
                        variant="outlined"
                        value={values.serviceProviderEmail ?? ''}
                        onChange={(e) => setFieldValue('serviceProviderEmail', e.target.value)}
                        onBlur={() => setFieldTouched('serviceProviderEmail', true)}
                        error={Boolean(fieldError('serviceProviderEmail'))}
                     />
                     {fieldError('serviceProviderEmail') && (
                        <FormHelperText sx={errorTextSx}>
                           {errors.serviceProviderEmail}
                        </FormHelperText>
                     )}
                  </Grid>
                  <Grid item md={4} xs={12}>
                     <label htmlFor="serviceProviderPhone">Phone Number*</label>
                     <TextField
                        fullWidth
                        id="serviceProviderPhone"
                        name="serviceProviderPhone"
                        variant="outlined"
                        value={values.serviceProviderPhone ?? ''}
                        onChange={(e) => setFieldValue('serviceProviderPhone', e.target.value)}
                        onBlur={() => setFieldTouched('serviceProviderPhone', true)}
                        error={Boolean(fieldError('serviceProviderPhone'))}
                     />
                     {fieldError('serviceProviderPhone') && (
                        <FormHelperText sx={errorTextSx}>
                           {errors.serviceProviderPhone}
                        </FormHelperText>
                     )}
                  </Grid>

                  {changed && (
                     <Grid item xs={12}>
                        <Box
                           sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
                        >
                           <Button
                              type="button"
                              variant="outlined"
                              onClick={() => setConfirmOpen(true)}
                              disabled={invalid}
                           >
                              Change
                           </Button>
                           <Typography variant="body2" color="text.secondary">
                              These differ from the saved details of {saved.name}. Click Change to
                              save them now; submitting the quotation saves them too.
                           </Typography>
                        </Box>
                     </Grid>
                  )}
               </>
            )}
         </Grid>

         <ConfirmDialog
            open={confirmOpen}
            title="Change service provider details?"
            confirmText="Yes, change"
            loading={isLoading}
            onConfirm={handleChange}
            onClose={() => setConfirmOpen(false)}
            message={
               <>
                  This will update the saved email and phone of <strong>{saved?.name}</strong> in
                  Service Providers. It is not limited to this quotation: every quotation and PDF
                  that uses this provider will show the new details.
               </>
            }
         >
            {saved && (
               <Box sx={{ mt: 2, fontSize: 14 }}>
                  <div>
                     <strong>Email:</strong> {saved.email || '-'} → {email}
                  </div>
                  <div>
                     <strong>Phone:</strong> {saved.phoneNo || '-'} → {phone}
                  </div>
               </Box>
            )}
         </ConfirmDialog>
      </Box>
   );
}

export default ServiceProviderSection;
